<template>
  <div class="puzzle-game">
    <h1>{{ mode }} Puzzle</h1>

    <div
      v-if="!gameCompleted"
      class="game-container"
    >
      <!-- Puzzle Board -->
      <div
        ref="puzzleBoard"
        class="puzzle-board"
      >
        <!-- Puzzle Grid -->
        <div
          class="puzzle-grid"
          :style="gridStyle"
        >
          <div
            v-for="(slot, index) in puzzleSlots"
            :key="index"
            class="puzzle-slot"
            :data-index="index"
            :style="slotStyle(slot)"
          >
            <div
              v-if="slot.piece"
              class="puzzle-piece"
              :style="pieceStyle(slot.piece)"
            />
          </div>
        </div>
      </div>

      <!-- Message Display -->
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>

      <!-- Puzzle Pieces -->
      <div class="pieces-container">
        <div
          v-for="(piece, index) in shuffledPieces"
          :key="index"
          class="piece-wrapper"
          :style="pieceWrapperStyle"
        >
          <div
            class="puzzle-piece draggable-piece"
            :style="pieceStyle(piece, false)"
            @mousedown="onMouseDown($event, piece)"
            @touchstart="onTouchStart($event, piece)"
            @click="speakOutLoud('Piece ' + (index + 1))"
          />
        </div>
      </div>
    </div>

    <!-- Game Completed Message -->
    <div
      v-else
      class="game-over"
    >
      <LevelComplete message="Congratulations!" />
    </div>
  </div>
</template>
<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  props: {
    mode: {
      type: String,
      default: "animals",
      validator: function (value) {
        return ["animals", "body", "planets"].includes(value);
      },
    },
  },
  data() {
    return {
      puzzleGridSize: 3, // Change to 2 or 3 for 4 or 9 pieces respectively
      puzzlePieces: [],
      shuffledPieces: [],
      puzzleSlots: [],
      draggedPiece: null,
      message: "",
      gameCompleted: false,
      selectedVoice: null,

      // New data properties for dragging
      isDragging: false,
      dragPiece: null,
      dragOffsetX: 0,
      dragOffsetY: 0,
    };
  },
  computed: {
    originalImage() {
      return this.getImageForMode();
    },
    gridStyle() {
      return {
        display: "grid",
        gridTemplateColumns: `repeat(${this.puzzleGridSize}, 1fr)`,
        gridTemplateRows: `repeat(${this.puzzleGridSize}, 1fr)`,
        width: "100%",
        height: "100%",
      };
    },
    boardStyle() {
      return {
        width: "100%",
        maxWidth: "400px",
        minWidth: "300px",
        position: "relative",
        aspectRatio: '1 / 1',
      };
    },
    pieceWrapperStyle() {
      const numPiecesPerRow = this.puzzleGridSize;
      const margin = 10; // Total margin per piece (left + right)
      const flexBasis = `calc(${100 / numPiecesPerRow}% - ${margin}px)`;
      return {
        flex: `1 0 ${flexBasis}`,
        maxWidth: flexBasis,
        margin: `${margin / 2}px`,
        position: 'relative',
        aspectRatio: '1 / 1',
      };
    },
  },
  mounted() {
    this.setupGame();
    this.setupVoices();
  },
  methods: {
    getImageForMode() {
      if (this.mode === "animals") {
        // Replace the URL below with the image of an animal
        return "https://png.pngtree.com/png-clipart/20230926/original/pngtree-adorable-fox-cartoon-for-kids-items-and-decor-vector-png-image_12879435.png";
      } else if (this.mode === "body") {
        // Current body image
        return "https://img.freepik.com/free-vector/little-afro-boy-yoga_24877-76398.jpg?t=st=1736958268~exp=1736961868~hmac=7dbe248418c608d7a293abdd15d5c900ecebab2e9fa98b0002963020cf83328a&w=826";
      } else if (this.mode === "planets") {
        // Replace the URL below with the image of a planet
        return "https://thumbs.dreamstime.com/b/cute-planets-funny-smiling-faces-solar-system-cute-cartoon-planets-funny-universe-kids-sun-pluto-mars-mercury-vector-141206010.jpg";
      } else {
        // Default image
        return "https://png.pngtree.com/png-clipart/20230926/original/pngtree-adorable-fox-cartoon-for-kids-items-and-decor-vector-png-image_12879435.png";
      }
    },
    setupGame() {
      this.gameCompleted = false;
      this.message = "";
      this.puzzlePieces = [];
      this.puzzleSlots = [];
      this.draggedPiece = null;

      // Create puzzle pieces data
      for (let row = 0; row < this.puzzleGridSize; row++) {
        for (let col = 0; col < this.puzzleGridSize; col++) {
          const piece = {
            id: row * this.puzzleGridSize + col,
            row: row,
            col: col,
            correctSlot: row * this.puzzleGridSize + col,
            isDragging: false,
            posX: null,
            posY: null,
          };
          this.puzzlePieces.push(piece);
          this.puzzleSlots.push({
            index: row * this.puzzleGridSize + col,
            piece: null,
          });
        }
      }

      // Shuffle pieces
      this.shuffledPieces = this.shuffleArray([...this.puzzlePieces]);
    },
    resetGame() {
      this.setupGame();
    },
    shuffleArray(array) {
      let currentIndex = array.length,
        temporaryValue,
        randomIndex;

      while (0 !== currentIndex) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;

        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
      }

      return array;
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    pieceStyle(piece, isSlot = true) {
      const gridSizeMinusOne = this.puzzleGridSize - 1;
      const bgSize = `${this.puzzleGridSize * 100}%`;
      const bgPosX =
        gridSizeMinusOne > 0
          ? `${(piece.col / gridSizeMinusOne) * 100}%`
          : "0%";
      const bgPosY =
        gridSizeMinusOne > 0
          ? `${(piece.row / gridSizeMinusOne) * 100}%`
          : "0%";

      const style = {
        width: "100%",
        height: "100%",
        backgroundImage: `url(${this.originalImage})`,
        backgroundSize: bgSize,
        backgroundPosition: `${bgPosX} ${bgPosY}`,
        backgroundRepeat: "no-repeat",
        border: !isSlot ? "1px solid #aaa" : undefined,
      };

      if (piece.isDragging) {
        const slotSize = this.getSlotSize();
        style.position = "fixed";
        style.left = piece.posX + "px";
        style.top = piece.posY + "px";
        style.zIndex = 1000;
        style.transform = "translate(0, 0)";
        style.width = `${slotSize}px`;
        style.height = `${slotSize}px`;
        style.pointerEvents = "none";
      }

      return style;
    },
    slotStyle(slot) {
      const gridSizeMinusOne = this.puzzleGridSize - 1;
      const bgSize = `${this.puzzleGridSize * 100}%`;
      const col = slot.index % this.puzzleGridSize;
      const row = Math.floor(slot.index / this.puzzleGridSize);
      const bgPosX =
        gridSizeMinusOne > 0 ? `${(col / gridSizeMinusOne) * 100}%` : "0%";
      const bgPosY =
        gridSizeMinusOne > 0 ? `${(row / gridSizeMinusOne) * 100}%` : "0%";

      let filterStyle = "grayscale(100%)";
      if (slot.piece) {
        filterStyle = "none";
      }

      return {
        backgroundImage: `url(${this.originalImage})`,
        backgroundSize: bgSize,
        backgroundPosition: `${bgPosX} ${bgPosY}`,
        backgroundRepeat: "no-repeat",
        filter: filterStyle,
        position: "relative",
      };
    },
    onMouseDown(event, piece) {
      event.preventDefault();
      this.isDragging = true;
      this.dragPiece = piece;
      const rect = event.target.getBoundingClientRect();
      this.dragOffsetX = event.clientX - rect.left;
      this.dragOffsetY = event.clientY - rect.top;

      // Add event listeners
      document.addEventListener("mousemove", this.onMouseMove);
      document.addEventListener("mouseup", this.onMouseUp);

      // Position the piece absolutely
      piece.isDragging = true;
      piece.posX = event.clientX - this.dragOffsetX;
      piece.posY = event.clientY - this.dragOffsetY;
    },
    onMouseMove(event) {
      if (this.isDragging && this.dragPiece) {
        this.dragPiece.posX = event.clientX - this.dragOffsetX;
        this.dragPiece.posY = event.clientY - this.dragOffsetY;
      }
    },
    onMouseUp(event) {
      if (this.isDragging) {
        // Remove event listeners
        document.removeEventListener("mousemove", this.onMouseMove);
        document.removeEventListener("mouseup", this.onMouseUp);

        // Handle drop
        this.handleDrop(event);

        this.isDragging = false;
        this.dragPiece = null;
      }
    },
    onTouchStart(event, piece) {
      event.preventDefault();
      this.isDragging = true;
      this.dragPiece = piece;
      const touch = event.touches[0];
      const rect = event.target.getBoundingClientRect();
      this.dragOffsetX = touch.clientX - rect.left;
      this.dragOffsetY = touch.clientY - rect.top;

      // Add event listeners
      document.addEventListener("touchmove", this.onTouchMove);
      document.addEventListener("touchend", this.onTouchEnd);

      // Position the piece absolutely
      piece.isDragging = true;
      piece.posX = touch.clientX - this.dragOffsetX;
      piece.posY = touch.clientY - this.dragOffsetY;
    },
    onTouchMove(event) {
      if (this.isDragging && this.dragPiece) {
        const touch = event.touches[0];
        this.dragPiece.posX = touch.clientX - this.dragOffsetX;
        this.dragPiece.posY = touch.clientY - this.dragOffsetY;
      }
    },
    onTouchEnd(event) {
      if (this.isDragging) {
        // Remove event listeners
        document.removeEventListener("touchmove", this.onTouchMove);
        document.removeEventListener("touchend", this.onTouchEnd);

        // Handle drop
        this.handleDrop(event);

        this.isDragging = false;
        this.dragPiece = null;
      }
    },
    handleDrop(event) {
      let clientX, clientY;
      if (event.type.startsWith("touch")) {
        const touch = event.changedTouches[0];
        clientX = touch.clientX;
        clientY = touch.clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }

      // Get the element at the drop point
      const element = document.elementFromPoint(clientX, clientY);

      if (element && element.classList.contains("puzzle-slot")) {
        const slotIndex = parseInt(element.dataset.index);
        if (
          slotIndex !== undefined &&
          !this.puzzleSlots[slotIndex].piece &&
          slotIndex === this.dragPiece.correctSlot
        ) {
          // Correct placement
          const slot = this.puzzleSlots[slotIndex];
          slot.piece = this.dragPiece;

          // Remove piece from shuffledPieces
          this.shuffledPieces = this.shuffledPieces.filter(
            (p) => p.id !== this.dragPiece.id
          );

          // Play success sound or give feedback
          this.playSound(
            "https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3"
          );

          // Check if puzzle is completed
          if (this.puzzleSlots.every((s) => s.piece !== null)) {
            this.gameCompleted = true;
            this.speakOutLoud("Congratulations!");
            setTimeout(() => {
              this.$router.push(`/${this.mode}`); // Adjust the route as necessary
            }, 4000);
          }
        } else {
          // Incorrect placement
          this.message = "❌";
          // Clear message after 2 seconds
          setTimeout(() => {
            this.message = "";
          }, 2000);
        }
      } else {
        // Dropped outside any slot
        // Return piece to its original position (do nothing)
      }
      // Reset dragging
      if (this.dragPiece) {
        this.dragPiece.isDragging = false;
        this.dragPiece.posX = null;
        this.dragPiece.posY = null;
      }
    },
    getSlotSize() {
      const board = this.$refs.puzzleBoard;
      if (board) {
        const { width } = board.getBoundingClientRect();
        const slotSize = width / this.puzzleGridSize;
        return slotSize;
      }
      return 100; // Default size
    },
  },
};
</script>
<style scoped>
.puzzle-game {
  text-align: center;
  width: 100%;
}

.game-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Updated .puzzle-board */
.puzzle-board {
  position: relative;
  width: 100%;
  max-width: 400px;
  min-width: 300px;
  margin-bottom: 20px;
  aspect-ratio: 1 / 1;
}

.puzzle-grid {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.puzzle-slot {
  border: 1px dashed #aaa;
  box-sizing: border-box;
  position: relative;
}

.puzzle-piece {
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
}

.pieces-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  max-width: 400px;
  min-width: 300px;
  position: relative;
}

.piece-wrapper {
  flex: 1 0 calc(33.333% - 10px); /* Adjusted based on puzzleGridSize */
  max-width: calc(33.333% - 10px);
  margin: 5px;
  position: relative;
  aspect-ratio: 1 / 1;
}

.draggable-piece {
  cursor: grab;
}

.draggable-piece:active {
  cursor: grabbing;
}

.message {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
}

.game-over {
  margin-top: 50px;
}

button {
  padding: 10px 20px;
  font-size: 1rem;
  cursor: pointer;
}
</style>